import { Controller } from "@hotwired/stimulus"

/**
 * Detects grid line breaks within category groups and marks
 * row-end / row-start cards so CSS can apply rounding, borders,
 * and "dissolving dots" continuation effects.
 * Also equalizes title-area heights per visual row so cards align.
 */
export default class extends Controller {
  static targets = ["card"]
  declare cardTargets: HTMLElement[]

  private observer?: ResizeObserver

  connect() {
    this.observer = new ResizeObserver(() => this.layout())
    this.observer.observe(this.element)
    // Initial detection after layout settles
    requestAnimationFrame(() => this.layout())
  }

  disconnect() {
    this.observer?.disconnect()
  }

  private layout() {
    this.detectWraps()
    this.equalizeRows()
  }

  private detectWraps() {
    const cards = this.cardTargets

    // Clear previous marks
    for (const card of cards) {
      delete card.dataset.rowEnd
      delete card.dataset.rowStart
    }

    // Group cards by category
    const groups = new Map<string, HTMLElement[]>()
    for (const card of cards) {
      const group = card.dataset.categoryGroup ?? "_"
      if (!groups.has(group)) groups.set(group, [])
      groups.get(group)!.push(card)
    }

    // Within each group, detect row breaks by comparing offsetTop
    for (const groupCards of groups.values()) {
      if (groupCards.length < 2) continue

      for (let i = 1; i < groupCards.length; i++) {
        const prev = groupCards[i - 1]
        const curr = groupCards[i]

        if (Math.abs(prev.offsetTop - curr.offsetTop) > 4) {
          // Row break between prev and curr — but only mark as
          // row-end/row-start if they aren't already the category edges
          // (CSS uses these attrs to show dissolving-dot pseudo-elements)
          if (!prev.hasAttribute("data-cat-last")) {
            prev.dataset.rowEnd = ""
          }
          if (!curr.hasAttribute("data-cat-first")) {
            curr.dataset.rowStart = ""
          }
        }
      }
    }
  }

  private equalizeRows() {
    const cards = this.cardTargets
    if (cards.length === 0) return

    // Reset any previously set heights so we measure natural sizes
    for (const card of cards) {
      const titleArea = card.firstElementChild as HTMLElement | null
      if (titleArea) titleArea.style.minHeight = ""
    }

    // Group cards into visual rows by offsetTop
    const rows: HTMLElement[][] = []
    let currentRow: HTMLElement[] = [cards[0]]
    let currentTop = cards[0].offsetTop

    for (let i = 1; i < cards.length; i++) {
      if (Math.abs(cards[i].offsetTop - currentTop) <= 4) {
        currentRow.push(cards[i])
      } else {
        rows.push(currentRow)
        currentRow = [cards[i]]
        currentTop = cards[i].offsetTop
      }
    }
    rows.push(currentRow)

    // For each row, equalize title-area heights
    for (const row of rows) {
      let maxTitleHeight = 0
      const titleAreas: HTMLElement[] = []

      for (const card of row) {
        const titleArea = card.firstElementChild as HTMLElement | null
        if (titleArea) {
          titleAreas.push(titleArea)
          maxTitleHeight = Math.max(maxTitleHeight, titleArea.offsetHeight)
        }
      }

      if (maxTitleHeight > 0) {
        for (const ta of titleAreas) {
          ta.style.minHeight = `${maxTitleHeight}px`
        }
      }
    }
  }
}
